"use strict";

import jQuery from 'jquery';

/**
 * Formular-Konfiguration. Siehe weitere Optionen in
 * {@link gc.gis.util.GCForm} und
 * {@link gc.gis.util.GCFormRenderer.renderControl}.
 *
 * @typedef gc.gis.util.GCFormConfig
 * @type {Object<string,*>}
 * @property {Array<Object<string,*>>} controls
 *      Liste von Control-Konfigurationen. Siehe {@link gc.gis.util.GCFormRenderer.renderControl}.
 * @property {function} submit
 *      Funktion, die beim Submit ausgeführt wird.
 * @property {function} [reset]
 *      Optionale Funktion, die beim Reset ausgeführt wird.
 * @property {string} [config.className]
 *      Optionale CSS-Klasse(n) für das <code>&lt;form&gt;</code>-Element.
 * @property {string} [config.method='GET']
 *      Optionales <code>method</code>-Attribut.
 */

/**
 * @classdesc
 * Klasse zum Rendern von Formularen.
 *
 * @static
 * @class
 * @memberof gc.gis.util
 * @author Anton Popov <a.popov@brain-scc.de>
 */
gc.gis.util.GCFormRenderer = {

	/**
	 * Rendert ein Formular-Element und gibt eine
	 * {@link gc.gis.util.GCForm}-Instanz zurück.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Formular-Konfiguration. Siehe weitere Optionen in
	 *      {@link gc.gis.util.GCForm} und
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}.
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das <code>&lt;form&gt;</code>-Element
	 *
	 * @param {string} [config.method='GET']
	 *      Optionales <code>method</code>-Attribut.
	 *
	 * @param {Object[]} config.controls
	 *      Siehe {@link gc.gis.util.GCFormRenderer.renderControl}.
	 *
	 * @return {gc.gis.util.GCForm}
	 */
	renderForm: function( config )
	{
		var self = this;

		var element_form = jQuery('<form>', {
			'class': 'gc-form'+ (config.className ? ' '+config.className : ''),
			method:  config.method || 'GET'
		});

		// Objekt zum vorhalten der Formularelemente mit Namen
		var form = new gc.gis.util.GCForm({
			config: config,
			element: element_form
		});

		config._element = element_form;

		var element_container = jQuery('<div>', {
			'class': 'gc-form-container'
		});
		var element_expandables = jQuery('<div>', {
			'class': 'gc-form-advanced-container'
		});
		var element_actions = jQuery('<div>', {
			'class': 'gc-form-actions form-inline'
		});
		element_form
			.append( element_container )
			.append( element_expandables )
			.append( element_actions );

		for( var i=0; i<config.controls.length; i++ )
		{
			var c = config.controls[i];

			var element;
			var is_action = false;
			var is_advanced = false;

			switch( c.type )
			{
				case 'submit':
					element = this.renderSubmit( c );
					is_action = true;
					break;
				case 'reset':
					element = this.renderReset( c );
					is_action = true;
					break;
				case 'advanced':
					element = this.renderControl( c, form );
					is_advanced = true;
					break;
				default:
					element = this.renderControl( c, form );
			}

			if( element )
			{
				if( is_action === true )
				{
					element_actions.append( element );
				}
				else if( is_advanced === true )
				{
					element_expandables.append( element );
				}
				else
				{
					element_container.append( element );
				}
			}
		}

		return form;
	},

	/**
	 * Rendert einen mit Controls gefüllten Container. Der Container kann einem
	 * Formular zugeordnet werden
	 * (siehe {@link gc.gis.util.GCFormRenderer.renderForm}) oder alleine
	 * stehen, wobei jedoch jedes Control seine eigenen Events verwalten muss
	 * und nicht durch ein <code>&lt;form&gt;</code>-Element gruppiert ist.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *
	 * @param {string} [config.className]
	 *      CSS-Klasse(n) für das Container-Element
	 *
	 * @param {Object[]} config.controls
	 *      Siehe {@link gc.gis.util.GCFormRenderer.renderControl}.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften der
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderFormContent: function( config, opt_form )
	{
		var element = jQuery('<div>', {
			'class': 'gc-form-content'+ (config.className ? ' '+config.className : '')
		});

		for( var i=0; i<config.controls.length; i++ )
		{
			element.append( this.renderControl( config.controls[i], opt_form ) );
		}
		config.getElement = function() {
			return this._element;
		};

		return element;
	},

	/**
	 * Wrapperfunktion für das Rendern ein Controls. Diese Funktion ist allen
	 * anderen <code>render&lt;CONTROL&gt;</code>-Funktionen zu bevorzugen, da
	 * eine Initialisierung mittels
	 * {@link gc.gis.util.GCFormRenderer.__initControl} stattfindet, sowie die
	 * Eigenschaften des jeweiligen Controls mit einer
	 * {@link gc.gis.util.GCForm}-Instanz bekannt gemacht werden können.
	 * <br><strong>Achtung:</strong> Diese Funktion berücksichtigt
	 * <em>NICHT</em> Controls der Typen <code>submit</code> und
	 * <code>reset</code>, da diese außerhalb eines Formular-Elementes keinen
	 * Sinn ergeben! Für derartige Buttons ist der Typ <code>button</code> oder
	 * die Verwendung der Funktion {@link gc.gis.util.GCFormRenderer.renderForm}
	 * zu bevorzugen. Siehe auch {@link gc.gis.util.GCFormRenderer.renderReset}
	 * bzw. {@link gc.gis.util.GCFormRenderer.renderSubmit}
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config Konfiguration des Controls. Siehe weitere Parameter je nach <code>type</code>.
	 *
	 * @param {string} config.name
	 *      Eindeutiges <code>name</code>-Attribut für die Zuordnung der Eigenschaft zum Formular.
	 *
	 * @param {string} config.label
	 *      Umschließendes Label. Siehe {@link gc.gis.util.GCFormRenderer.renderLabelFor}.
	 *
	 * @param {string} config.type    Gültige Werte:
	 *    <dl>
	 * 	    <dt><code>group</code> 	       </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderGroup}</dd>
	 * 	    <dt><code>advanced</code>      </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderAdvanced}</dd>
	 * 	    <dt><code>static</code>        </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderStatic}</dd>
	 * 	    <dt><code>hidden</code>        </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderHidden}</dd>
	 * 	    <dt><code>text</code>          </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderInputText}</dd>
	 * 	    <dt><code>number</code>        </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderInputText}</dd>
	 * 	    <dt><code>url</code>           </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderInputText}</dd>
	 * 	    <dt><code>file</code>          </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderFile}</dd>
	 * 	    <dt><code>textarea</code>      </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderTextArea}</dd>
	 * 	    <dt><code>select</code>        </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderSelect}</dd>
	 * 	    <dt><code>input-select</code>  </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderInputSelect}</dd>
	 * 	    <dt><code>radio</code>         </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderRadioButtons}</dd>
	 * 	    <dt><code>checkbox</code>      </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderCheckbox}</dd>
	 * 	    <dt><code>checkboxgroup</code> </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderCheckboxGroup}</dd>
	 * 	    <dt><code>button</code>        </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderButton}</dd>
	 * 	    <dt><code>custom</code>        </dt><dd>für weitere Parameter siehe {@link gc.gis.util.GCFormRenderer.renderCustom}</dd>
	 *    </dl>
	 *
	 * @param {string} [config.id]
	 *      HTML-ID.
	 *
	 * @param {string} [config.alt]
	 *      <code>alt</code>-Attribut.
	 *
	 * @param {string} [config.tooltip]
	 *      <code>title</code>-Attribut.
	 *
	 * @param {function} [config.click]
	 *      Was passiert bei <code>jQuery.on('click', ... )</code>?
	 *
	 * @param {function} [config.change]
	 *      Was passiert bei <code>jQuery.on('change', ... )</code>?
	 *
	 * @param {function} [config.initFunc]
	 *      Funktion, die beim Initialisieren aufgerufen wird. Geeignet zum
	 *      setzen von Listenern.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften des
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderControl: function( control, opt_form )
	{
		var element;

		switch( control.type )
		{
			case 'group':
				element = this.renderGroup( control, opt_form );
				break;
			case 'advanced':
				element = this.renderAdvanced( control, opt_form );
				break;
			case 'static':
				element = this.renderStatic( control, opt_form );
				break;
			case 'hidden':
				element = this.renderHidden( control, opt_form );
				break;
			case 'text':
				element = this.renderInputText( control, opt_form );
				break;
			case 'number':
				element = this.renderInputText( control, opt_form );
				break;
			case 'url':
				element = this.renderInputText( control, opt_form );
				break;
			case 'file':
				element = this.renderFile( control, opt_form );
				break;
			case 'textarea':
				element = this.renderTextArea( control, opt_form );
				break;
			case 'select':
				element = this.renderSelect( control, opt_form );
				break;
			case 'input-select':
				element = this.renderInputSelect( control, opt_form );
				break;
			case 'radio':
				element = this.renderRadioButtons( control, opt_form );
				break;
			case 'checkboxgroup':
				element = this.renderCheckboxGroup( control, opt_form );
				break;
			case 'checkbox':
				element = this.renderCheckbox( control, opt_form );
				break;
			case 'button':
				element = this.renderButton( control, opt_form );
				break;
			case 'custom':
				element = this.renderCustom( control, opt_form );
				break;
			case 'scale-selector':
				element = this.renderScaleSelector( control, opt_form );
				break;
			case 'layer-selector':
				element = this.renderLayerSelector( control, opt_form );
				break;
		}

		// wird doch in __initControl gemacht (08.09.2017)
		// control.getElement = function() {
		// 	return this._element;
		// };

		// Init
		this.__initControl( control );

		// Ggf. im Formularobjekt merken
		this.__storeInForm( control, opt_form );

		return element;
	},

	/**
	 * Rendert ein Text-Input-Element.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Siehe {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} [config.placeholder]
	 *      <code>placeholder</code>-Attribut.
	 *
	 * @param {number} [config.size]
	 *      <code>size</code>-Attribut.
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das Control-Element
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften des
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderInputText: function( config, opt_form )
	{
		var element_input = jQuery('<input>', {
			'class': 'form-control control-input'+ (config.className ? ' '+config.className : ''),
			type: 'text'
		});
		this.__setupCommonAttributes( element_input, config );
		if( config.placeholder )
		{
			element_input.attr('placeholder', config.placeholder );
		}
		if( config.size )
		{
			element_input.attr('size', config.size );
		}

		config._element = element_input;

		var element = this.renderLabelFor( element_input, config );

		return element;
	},

	/**
	 * Rendert eine Textarea.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Siehe {@link gc.gis.util.GCFormRenderer.renderControl}.
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das Control-Element.
	 *
	 * @param {string} [config.placeholder]
	 *      <code>placeholder</code>-Attribut.
	 *
	 * @param {number} [config.size]
	 *      <code>size</code>-Attribut.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften des
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderTextArea: function( config, opt_form )
	{
		var element_input = jQuery('<textarea>', {
			'class': 'form-control control-textarea'+ (config.className ? ' '+config.className : '')
		});
		this.__setupCommonAttributes( element_input, config );
		if( config.placeholder )
		{
			element_input.attr('placeholder', config.placeholder );
		}
		if( config.size )
		{
			element_input.attr('rows', config.size );
		}

		config._element = element_input;

		var element = this.renderLabelFor( element_input, config );

		return element;
	},

	/**
	 * Typdefinition für die Konfiguration eines
	 * <code>&lt;option&gt;</code>-Elements eines
	 * <code>&lt;select&gt;</code>-Elements.
	 *
	 * @typedef gc.gis.util.GCSelectOption
	 * @property {string} label Angezeigter Text
	 * @property {*} value Primitive
	 * @property {Object} data  Complex
	 * @property {boolean} [default=false] Bei non-multiple
	 * @property {boolean} [selected=false] Bei multiple
	 */

	/**
	 * Rendert ein <code>&lt;select&gt;</code>-Element.
	 *
	 * <br><strong>Hinweis:</strong> Dem übergebenen Control-Objekt wird eine
	 * Funktion <code>updateOptions</code> angefügt, die eine Liste von Objekten
	 * des Typs {@link gc.gis.util.GCSelectOption} als Parameter erwartet, diese
	 * in <code>option</code>-Elemente umwandelt und sie dem
	 * <code>select</code>-Element anfügt. Alte <code>option</code>-Elemente
	 * werden vorher entfernt. Kurz gesagt: der Aufruf der Funktion
	 * <code>updateOptions</code> aktualisiert den Inhalt des Controls.
	 * In Kombination mit <code>updateFunc</code>, die eine Liste von Objekten
	 * des Typs {@link gc.gis.util.GCSelectOption} zurückgibt, lässt sich das
	 * Control dynamisch gestalten.
	 *
	 * @example <caption>Dynamischer Inhalt:</caption>
	 * // Control-Definition
	 * var control = {
	 *     type: 'select',
	 *     label: 'Foo',
	 *     options: [
	 *         // initiale Items
	 *     ],
	 *     updateFunc: function() {
	 *         return [
	 *             // aktualisierte Items
	 *         ];
	 *     }
	 * };
	 * // Control rendern (updateOptions() wird erstellt)
	 * gc.gis.util.GCFormRenderer.renderSelect( control );
	 * // Control updaten
	 * control.updateOptions( control.updateFunc() )
	 *
	 * @example <caption>Dynamischer Inhalt mit initial registriertem Listener:</caption>
	 * // Control-Definition
	 * var control = {
	 *     type: 'select',
	 *     label: 'Foo',
	 *     initFunc: function( control ) {
	 *         gc.gis.iface.modules.MyModule.on('my:module:event', control, function( evt ) {
	 *             control.updateOptions( control.updateFunc( evt.myParam1, evt.myParam2 ) );
	 *         });
	 *     },
	 *     updateFunc: function( myParam1, myParam2 ) {
	 *         // Achtung: myParam1/2 werden beim initialen Rendern 'undefined' sein,
	 *         //          da der initiale Aufruf innerhalb von gc.gis.util.GCFormRenderer.renderSelect()
	 *         //          parameterlos erfolg.
	 *         return [
	 *             // erstelle Items
	 *         ];
	 *     }
	 * };
	 * // Control rendern (updateOptions() wird erstellt)
	 * gc.gis.util.GCFormRenderer.renderSelect( control );
	 * // Control wird beim jeweiligen Listener-Event automatisch geupdatet.
	 *
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das Control-Element.
	 *
	 * @param {string}           [config.placeholder]
	 *      <code>placeholder</code>-Attribut.
	 *
	 * @param {number}           [config.size]
	 *      <code>size</code>-Attribut.
	 *
	 * @param {boolean}          [config.multiple=false]
	 *      <code>multiple</code>-Attribut setzen?
	 *
	 * @param {gc.gis.util.GCSelectOption} [config.options]
	 *      Liste von Elementen.
	 *      <br><strong>Achtung:</strong> Dieser Parameter ist zwar als optional
	 *      geflagt, es muss allerdings entweder <code>updateFunc</code>
	 *      oder <code>options</code> definiert sein. Sind beide definiert, so
	 *      wird <code>options</code> von <code>updateFunc</code> überschrieben.
	 *
	 * @param {Function}         [config.updateFunc]
	 *      Funktion zum Befüllen von Parameter <code>options</code>.
	 *      Es muss eine Liste von Objekten des Typs
	 *      {@link gc.gis.util.GCSelectOption} zurückgegeben werden.
	 *      <br><strong>Hinweis:</strong> Diese Funktion wird initial, ohne
	 *      Parameter aufgerufen, sofern definiert. Bei manuellem Aufruf (siehe
	 *      z.B. Beispiel 2) können beliebige Parameter übergeben werden.
	 *      <br><strong>Achtung:</strong> Dieser Parameter ist zwar als optional
	 *      geflagt, es muss allerdings entweder <code>updateFunc</code>
	 *      oder <code>options</code> definiert sein. Sind beide definiert, so
	 *      wird <code>options</code> von <code>updateFunc</code> überschrieben.
	 *
	 * @param {*}                [config.default]
	 *      <code>value</code> des Default-Werts (siehe Parameter <code>options</code>)
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften des
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderSelect: function( config, opt_form )
	{
		const select = new gc.gis.widget.GCSelect(config);
		const containerElement = select.getElement();

		if (typeof config.updateFunc === 'function')
		{
			select.updateOptions(config.updateFunc.call(this));
		}
		config.updateOptions = function(items) {
			select.updateOptions(items);
		}

		// Widget
		config._widget = select;

		// Control merken (für das Formular ist nur das Input-Element relevant)
		config._element = select.getElementInput();

		return containerElement;
	},

	/**
	 * Rendet ein Steuerelement zu Eingabe eines Maßstabs auf Basis des Widgets
	 * <code>gc.gis.widget.GCScaleSelector<code>.
	 *
	 * @param {Object<string,*>} config Aufrufoptionen
	 * @param {gc.gis.map.GCMapScale[]} [config.scales=[]] Liste der Maßstäbe
	 * @param {string} [config.direction='down'] Aufklapprichtung 'down' oder 'up'
	 * @param {string} [config.inputClassName] zusätzliche CSS-Klasse(n), die am Input-Element gesetzt wird/werden
	 * @param {function} [config.change] Funktion, die bei Änderung des Maßstabs aufgerufen werden soll
	 * @param {string} [config.userdefinedScale=true] Flag um freie Eigabe des Maßstabs durch Nutzer zu erlauben
	 * @param {Number} [config.minScale] minimaler Maßstab, den der Nutzer eingeben kann
	 * @param {Number} [config.maxScale] maximaler Maßstab, den der Nutzer eingeben kann
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften des
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderScaleSelector: function(config, opt_form)
	{
		var selector = new gc.gis.widget.GCScaleSelector(config);

		// Widget
		config._widget = selector;

		// Control merken (für das Formular ist nur das Input-Element relevant)
		config._element = selector.getElementInput();

		// In Label verpacken
		return this.renderLabelFor(selector.getElement(), config);
	},

	/**
	 * Rendet ein Steuerelement zu Auswahl eines Layers über eine Auswahlliste
	 * <code>gc.gis.widget.GCLayerSelector<code>.
	 *
	 * @param config
	 * @param opt_form
	 * @return {jQuery}
	 */
	renderLayerSelector: function(config, opt_form)
	{
		var selector = new gc.gis.widget.GCLayerSelector(config);
		var element = selector.getElement();    // getElement() erzeugt alle notwendigen Elemente des Controls

		// Widget
		config._widget = selector;

		// Control merken (für das Formular ist nur das Input-Element relevant)
		config._element = selector.getElementInput();

		return element;
	},

	/**
	 * TODO
	 *
	 * @public
	 * @method
	 *
	 */
	renderInputSelect: function( config, opt_form )	// FIXME: implement
	{
	},

	/**
	 * Typdefinition für die Konfiguration eines Radiobuttons.
	 *
	 * @typedef gc.gis.util.GCRadioItem
	 * @type {Object<string,*>}
	 * @property {string}           label
	 * @property {*}                value
	 * @property {Object<string,*>} [subForm]
	 * @property {boolean}          [default] Wenn <code>true</code> wird der Button vorausgewählt. <br><strong>Achtung:</strong> Aufpassen bei Mehrfachauswahl!
	 * @property {string}           [tooltip] Optionales <code>title</code>-Attribut.
	 * @property {function}         [change]
	 */

	/**
	 * Rendert zusammengehörige Radiobuttons.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für die Radiobuttons.
	 *
	 * @param {gc.gis.util.GCRadioItem[]} [config.options]
	 *      Liste von Radio-Buttons.
	 *      <br><strong>Achtung:</strong> Dieser Parameter ist zwar als optional
	 *      geflagt, es muss allerdings entweder <code>updateFunc</code>
	 *      oder <code>options</code> definiert sein. Sind beide definiert, so
	 *      wird <code>options</code> von <code>updateFunc</code> überschrieben.
	 *
	 * @param {function}                  [config.updateFunc]
	 *      Funktion zum Befüllen von Parameter <code>options</code>.
	 *      Es muss eine Liste von Objekten des Typs
	 *      {@link gc.gis.util.GCRadioItem} zurückgegeben werden.
	 *      <br><strong>Achtung:</strong> Dieser Parameter ist zwar als optional
	 *      geflagt, es muss allerdings entweder <code>updateFunc</code>
	 *      oder <code>options</code> definiert sein. Sind beide definiert, so
	 *      wird <code>options</code> von <code>updateFunc</code> überschrieben.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften der
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderRadioButtons: function( config, opt_form )
	{
		// Container rendern
		var element_fieldset = jQuery('<div>', {
			'class': 'gc-control-radio-group'
		});
		// Ggf. Container-Label
		if( config.label )
		{
			element_fieldset.html( jQuery('<label>', {
				html: '<span>'+ config.label +'</span> '
			}) );
		}

		var element_content = jQuery('<p>', {
			'class': 'form-inline'
		});
		element_fieldset.append( element_content );

		// Ggf. Optionen überschreiben
		if( typeof config.updateFunc === 'function')
		{
			config.options = config.updateFunc.call(this);
		}

		// Sub-Forms
		var sub_forms = [];
		var element_sub_content = jQuery('<div>');

		// Optionen rendern
		for( var i=0; i<config.options.length; i++ )
		{
			var element_input = jQuery('<input>', {
				'class': 'form-control control-radio'+ (config.className ? ' '+config.className : ''),
				type:    'radio',
				value:   config.options[i].value
			});
			config.options[i]._element = element_input;
			config.options[i].getElement = function() {
				return this._element;
			};

			element_input = this.__setupCommonAttributes( element_input, {name: config.name} );

			// Ggf. Vorauswahl treffen
			if( config.options[i].default )
			{
				element_input.attr('checked', true );
			}

			// In Label verpacken
			var element_label = this.renderLabelFor( element_input, config.options[i], true );
			if( config.options[i].tooltip )
			{
				element_label.attr('title', config.options[i].tooltip );
			}

			// Mit Leerzeichen trennen
			if( i > 0 )
			{
				element_content.append(' ');
			}
			element_content.append( element_label );

			// Ggf. change-event anhängen
			if( typeof config.options[i].change === 'function')
			{
				this.__setupChangeEvent( config.options[i], config.options[i].change, opt_form );
			}

			// Ggf. Sub-Forms rendern
			if( config.options[i].subForm )
			{
				var sub_form = this.renderSubForm(
					config.options[i].subForm,
					element_input,
					element_sub_content,
					opt_form
				);
				if( config.options[i].default )
				{
					element_sub_content.append( sub_form );
				}
				sub_forms.push( sub_form );
			}

			// Init-Function
			this.__initControl( config.options[i] );
		}

		// Ggf. Sub-Forms hinzufügen
		if( sub_forms.length > 0 )
		{
			element_fieldset.append( element_sub_content );
		}

		return element_fieldset;
	},

	/**
	 * Typdefinition für die Konfiguration einer Checkbox.
	 *
	 * @typedef gc.gis.util.GCCheckboxItem
	 * @type {Object<string,*>}
	 * @property {string}           label
	 * @property {*}                value
	 * @property {Object<string,*>} [subForm]
	 * @property {boolean}          [active]  Checked?
	 * @property {string}           [tooltip] Optionales <code>title</code>-Attribut.
	 * @property {function}         [change]
	 */

	/**
	 * Rendert eine Gruppe von Checkboxen, die sich das gleiche
	 * <code>name</code>-Attribut teilen.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für die Checkboxen.
	 *
	 * @param {gc.gis.util.GCCheckboxItem[]} [config.options]
	 *      Liste der Checkboxen
	 *      <br><strong>Achtung:</strong> Dieser Parameter ist zwar als optional
	 *      geflagt, es muss allerdings entweder <code>updateFunc</code>
	 *      oder <code>options</code> definiert sein. Sind beide definiert, so
	 *      wird <code>options</code> von <code>updateFunc</code> überschrieben.
	 *
	 * @param {function}                  [config.updateFunc]
	 *      Funktion zum Befüllen von Parameter <code>options</code>.
	 *      Es muss eine Liste von Objekten vom Typ
	 *      {@link gc.gis.util.GCCheckboxItem} zurückgegeben werden.
	 *      <br><strong>Achtung:</strong> Dieser Parameter ist zwar als optional
	 *      geflagt, es muss allerdings entweder <code>updateFunc</code>
	 *      oder <code>options</code> definiert sein. Sind beide definiert, so
	 *      wird <code>options</code> von <code>updateFunc</code> überschrieben.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften der
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderCheckboxGroup: function( config, opt_form )
	{
		// Container rendern
		var element_fieldset = jQuery('<div>', {
			'class': 'gc-control-checkbox-group'
		});
		// Ggf. Container-Label
		if( config.label )
		{
			element_fieldset.html( jQuery('<label>', {
				html: '<span>'+ config.label +'</span> '
			}) );
		}

		var element_content = jQuery('<p>');
		element_fieldset.append( element_content );

		// Ggf. Optionen überschreiben
		if( typeof config.updateFunc === 'function')
		{
			config.options = config.updateFunc.call(this);
		}

		// Sub-Forms
		var sub_forms = [];
		var element_sub_content = jQuery('<div>');

		// Optionen rendern
		for( var i=0; i<config.options.length; i++ )
		{
			var element_input = jQuery('<input>', {
				'class': 'form-control control-checkbox'+ (config.className ? ' '+config.className : ''),
				type:    'checkbox',
				value:   config.options[i].value
			});
			config.options[i]._element = element_input;
			config.options[i].getElement = function() {
				return this._element;
			};

			element_input = this.__setupCommonAttributes( element_input, {name: config.name} );
			if( config.options[i].active )
			{
				element_input.attr('checked', true );
			}

			var element_label = this.renderLabelFor( element_input, config.options[i], true );
			if( config.options[i].tooltip )
			{
				element_label.attr('title', config.options[i].tooltip );
			}

			// Mit Leerzeichen trennen
			if( i > 0 )
			{
				element_content.append(' ');
			}
			element_content.append( element_label );

			// Ggf. change-event anhängen
			if( typeof config.options[i].change === 'function')
			{
				this.__setupChangeEvent( config.options[i], config.options[i].change, opt_form );
			}

			// Ggf. Sub-Forms rendern
			if( config.options[i].subForm )
			{
				var sub_form = this.renderSubForm(
					config.options[i].subForm,
					element_input,
					element_sub_content,
					opt_form
				);
				if( config.options[i].default )
				{
					element_sub_content.append( sub_form );
				}
				sub_forms.push( sub_form );
			}

			// Init-Function
			this.__initControl( config.options[i] );
		}

		// Ggf. Sub-Forms hinzufügen
		if( sub_forms.length > 0 )
		{
			element_fieldset.append( element_sub_content );
		}

		return element_fieldset;
	},

	/**
	 * Rendert eine Checkbox.
	 *
	 * @public
	 * @method
	 *
	 * @param {gc.gis.util.GCCheckboxItem} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das Control-Element.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften des
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderCheckbox: function( config, opt_form )
	{
		var element_input = jQuery('<input>', {
			'class': 'form-control control-checkbox'+ (config.className ? ' '+config.className : ''),
			type:    'checkbox',
			value:   config.value
		});
		config._element = element_input;
		element_input = this.__setupCommonAttributes( element_input, {name: config.name} );
		if( config.active )
		{
			element_input.attr('checked', true );
		}

		var element_label = this.renderLabelFor( element_input, config, true );

		// Ggf. change-event anhängen
		if(typeof config.change === 'function')
		{
			this.__setupChangeEvent(config, config.change, opt_form);
		}

		var element;
		if( config.subForm )
		{
			element = jQuery('<div>', {
				html: element_label
			});

			var element_container = jQuery('<div>');
			element.append( element_container );

			var sub_form = this.renderSubForm(
				config.subForm,
				element_input,
				element_container,
				opt_form
			);
			if( config.default )
			{
				element_container.append( sub_form );
			}
		}
		else
		{
			element = element_label;
		}

		if( config.tooltip )
		{
			element.attr('title', config.tooltip );
		}

		return element;
	},

	/**
	 * Rendert ein Input-Element für Dateiuploads.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das Control-Element.
	 *
	 * @param {string} [config.accept=undefined]
	 *      Komma-separierte Liste von Mime-Typen/Dateiendungen.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften des
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderFile: function( config, opt_form )
	{
		var element_input = jQuery('<input>', {
			'class': 'form-control control-file'+ (config.className ? ' '+config.className : ''),
			type: 'file'
		});
		this.__setupCommonAttributes( element_input, config );
		element_input.attr('accept', config.accept );

		config._element = element_input;

		this.__setupChangeEvent( config, config.change, opt_form )

		var element = this.renderLabelFor( element_input, config );
		if( config.tooltip )
		{
			element.attr('title', config.tooltip );
		}

		return element;
	},

	/**
	 * Rendert ein <code>&ltinput type="hidden"&gt;</code>-Element.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} config.name
	 *      Eigenschaftsschlüssel.
	 *
	 * @param {*} config.value
	 *      Eigenschaftswert.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften des
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderHidden: function( config, opt_form )
	{
		var element = jQuery('<input>', {
			type:  'hidden',
			name:  config.name,
			value: config.value
		});
		config._element = element;

		return element;
	},

	/**
	 * Rendert einen Button.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} config.label
	 *      Button-Text.
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das Control-Element.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften des
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderButton: function( config, opt_form )
	{
		var element = jQuery('<button>', {
			'class': 'form-control gc-control-button btn btn-default'+ (config.className ? ' '+config.className : ''),
			type: 'button',
			html: '<span>'+ config.label +'</span>'
		});
		this.__setupCommonAttributes( element, config );
		config._element = element;

		//this.__setupClickEvent( element, config.change );	// siehe __initControl

		return element;
	},

	/**
	 * Rendert einen statischen Text, der einem Label zugeordnet ist.
	 * Ist eine statische Eigenschaft definiert, wird deren Name bzw. Wert in
	 * einem <code>&ltinput type="hidden"&gt;</code> versteckt und geht damit
	 * ggf. in die übergebene Formular-Instanz ein.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} config.label
	 *      Angezeigter Text.
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das Container-Element.
	 *
	 * @param {string} [config.name]
	 *      Name der Eigenschaft. Wenn gesetzt, wird
	 *      {@link gc.gis.util.GCFormRenderer} aufgerufen.
	 *
	 * @param {*} [config.value]
	 *      Wert der Eigenschaft.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften des
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderStatic: function( config, opt_form )
	{
		var element = jQuery('<div>', {
			'class': 'form-group'+ (config.className ? ' '+config.className : ''),
			html: '<label class="control-label">'+ config.label +'</label> <p class="form-control-static">'+ (config.display_value ? config.display_value : config.value) +'</p>'
		});
		if( config.name )
		{
			var element_hidden = this.renderHidden( config, opt_form );

			element.append( element_hidden );

			config._element = element_hidden;
		}
		else
		{
			config._element = element;
		}

		return element;
	},

	/**
	 * Rendert ein in einem Container-DIV gewrapptes Sub-Formular.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das Container-Element.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften der
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderGroup: function( config, opt_form )
	{
		// Container rendern
		var element = jQuery('<div>', {
			'class': 'gc-control-group'+ (config.className ? ' '+config.className : '')
		});
		// Ggf. Container-Label
		if( config.label )
		{
			element.html( jQuery('<label>', {
				html: '<span>'+ config.label +'</span> '
			}) );
		}

		// Inhalte rendern
		var element_content = this.renderFormContent( config, opt_form );

		element.append( element_content );

		// Control merken
		//config._element = config.controls;	// FIXME: hatte das einen Grund?
		config._element = element;

		return element;
	},

	/**
	 * Rendert ein aufklappbares Sub-Formular.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das Container-Element.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften der
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderAdvanced: function( config, opt_form )
	{
		// Container rendern
		var element = jQuery('<div>', {
			'class': 'gc-control-advanced'+ (config.active ? ' gc-expanded' : ' gc-collapsed') + (config.className ? ' '+config.className : '')
		});

		// Inhalte rendern
		var element_content = this.renderFormContent( config, opt_form );
		element_content.addClass('gc-control-advanced-content form-container');
		if( !config.active )
		{
			element_content.hide();	// initial verstecken
		}

		// Ein-/Ausklapp-Label rendern
		element.append( jQuery('<strong>', {
			'class': 'gc-control-advanced-label',
			html:    '<span class="glyphicon"></span><span>'+ ( config.label ? config.label : 'Erweitert') +'</span>',
			click: function( evt ) {
				try
				{
					if( element_content.is(':visible') )
					{
						element.removeClass('gc-expanded').addClass('gc-collapsed');
						element_content.hide('blind');
					}
					else
					{
						element_content.show('blind');
						element.removeClass('gc-collapsed').addClass('gc-expanded');
					}
				}
				catch( err )
				{
					gc.gis.iface.log.error( this, err );
				}
			}
		}) );
		element.append( element_content );

		// Control merken
		//config._element = config.controls;	// FIXME: hatte das einen Grund?
		config._element = element;

		return element;
	},

	/**
	 * Rendert ein Sub-Formular, das durch das jQuery-<code>change</code>-Event
	 * eines Input-Elements entweder angezeigt oder versteckt wird.
	 * Für permanent sichtbare "Unterformulare" eignen sich
	 * {@link gc.gis.util.GCFormRenderer.renderAdvanced} oder
	 * {@link gc.gis.util.GCFormRenderer.renderGroup} besser.
	 *
	 * @public
	 * @method
	 *
	 * @param {gc.gis.util.GCFormConfig} config
	 *      Konfiguration des Sub-Formulars. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderFormContent} und
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {jQuery} element_input
	 *      jQuery-Element, dem <code>jQuery.on('change', ... )</code>
	 *      zugeordnet wird. Wenn das Element beim <code>change</code>-Event
	 *      <code>:checked</code> erfüllt, werden die Kindelemente von
	 *      <code>element_container</code> mit <code>jQuery.detach()</code>
	 *      entfernt und durch das aus <code>config</code> gerenderte
	 *      Sub-Formular ersetzt. Falls nicht, wird das gerenderte Sub-Formlar
	 *      von <code>element_container</code> mit <code>jQuery.detach()</code>
	 *      entfernt.
	 *
	 * @param {jQuery} element_container
	 *      jQuery-Element, dem das gerenderte Sub-Formular an-/abgehangen wird.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften der
	 *      Controls des Sub-Formulars zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderSubForm: function( config, element_input, element_container, opt_form )
	{
		var form = this.renderFormContent( config, opt_form );
		form.addClass('gc-subform');

		if (!element_input.is(':checked'))
		{
			form.hide();
		}

		var setup = function( e, f ) {
			e.on('change', function(evt) {
				try
				{
					if( jQuery(this).is(':checked') )
					{
						element_container.children().detach();
						element_container.append(f);
						f.show('blind');
					}
					else
					{
						f.hide(400, function() { f.detach() });
					}
				}
				catch(err)
				{
					gc.gis.iface.log.error(this, err);
				}
			});
		};
		setup( element_input, form );

		return form;
	},

	/**
	 * Rendert ein beliebiges Element.
	 * <br><span>Achtung:</span> Das Element wird
	 * nicht wirklich "gerendert". Eine Bessere Beschreibung wäre:
	 * Bereitet ein beliebiges Element so auf, dass es mit dem zugeordneten
	 * Control korrekt verbunden ist.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {jQuery} config.element
	 *      jQuery-Element mit beliebigem Inhalt.
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, dem die Eigenschaften des
	 *      Controls zugeordnet werden sollen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderCustom: function( config, opt_form )
	{
		var element = config.element;
		element = this.__setupCommonAttributes( element, config );
		config._element = element;

		return element;
	},

	/**
	 * Rendert einen Submit-Button.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} config.label
	 *      Button-Text.
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das Control-Element.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderSubmit: function( config )
	{
		var element = jQuery('<input>', {
			'class': 'form-control gc-control-button btn btn-success control-submit'+ (config.className ? ' '+config.className : ''),
			value:   config.label,
			type:    'submit'
		});
		element = this.__setupCommonAttributes( element, config );
		config._element = element;
		config.getElement = function() {
			return this._element;
		};

		return element;
	},

	/**
	 * Rendert einen Reset-Button.
	 *
	 * @public
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls. Für weitere Parameter siehe
	 *      {@link gc.gis.util.GCFormRenderer.renderControl}
	 *
	 * @param {string} config.label
	 *      Button-Text.
	 *
	 * @param {string} [config.className]
	 *      Optionale CSS-Klasse(n) für das Control-Element.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderReset: function( config )
	{
		var element = jQuery('<input>', {
			'class': 'form-control gc-control-button btn btn-primary control-reset'+ (config.className ? ' '+config.className : ''),
			value:   config.label,
			type:    'reset'
		});
		element = this.__setupCommonAttributes( element, config );
		config._element = element;
		config.getElement = function() {
			return this._element;
		};

		return element;
	},

	/**
	 * Rendert ein <code>&lt;label&gt;</code>-Element und schließt das
	 * übergebene Control darin ein.
	 *
	 * @public
	 * @method
	 *
	 * @param {jQuery} element_control
	 *      Mit <code>&lt;label&gt;</code> zu umschließendes jQuery-Element
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Labels.
	 *
	 * @param {jQuery|string} config.label
	 *      Element/String, d[as/er] den Labeltext beinhaltet.
	 *
	 * @param {string} [config.id]
	 *      Optionale ID des zu umschließenden jQuery-Elements. Wenn gesetzt,
	 *      wird das <code>for</code>-Attributs des Labels gesetzt.
	 *
	 * @param {jQuery|string} [config.prefix]
	 *      Optionale[s/r] Element/String, d[as/er] vor
	 *      <code>element_control</code> (in einem <code>&lt;span&gt;</code>
	 *      gewrappt) zum Label hinzugefügt wird.
	 *
	 * @param {jQuery|string} [config.suffix]
	 *      Optionale[s/r] Element/String, d[as/er] nach
	 *      <code>element_control</code> (in einem <code>&lt;span&gt;</code>
	 *      gewrappt) zum Label hinzugefügt wird.
	 *
	 * @param {boolean} [opt_after=false]
	 *      Optionales Flag, ob der Labeltext nach <code>element_control</code>
	 *      zum <code>&lt;label&gt;</code> hinzugefügt wird. Geeignet für z.B.
	 *      Checkboxen.
	 *
	 * @return {jQuery} jQuery-Container-Element
	 */
	renderLabelFor: function( element_control, config, opt_after )
	{
		var element_label = jQuery('<span>', {
			'class': 'control-label',
			html: config.label
		});

		var element = jQuery('<label>');
		var element_container;

		if( !config.prefix && !config.suffix )
		{
			element_container = element;
			element_container.append( element_label );
		}
		else
		{
			element_container = jQuery('<span>', {
				'class': 'input-group'
			});

			element.append( element_label );
			element.append( element_container );
		}

		if( !opt_after )
		{
			element_container.append(' ');
			element_container.append( element_control );
		}
		else
		{
			element_container.prepend(' ');
			element_container.prepend( element_control );
		}

		if( config.prefix )
		{
			element_container.prepend(' ');
			element_container.prepend( jQuery('<span>', {
				'class': 'input-group-addon',
				html: config.prefix
			}) );
		}
		if( config.suffix )
		{
			element_container.append(' ');
			element_container.append( jQuery('<span>', {
				'class': 'input-group-addon',
				html: config.suffix
			}) );
		}

		if( config.id )
		{
			element.attr('for', config.id );
		}

		return jQuery('<div>', {
			'class': 'form-group',
			html: element
		});
	},

	/**
	 * Setzt Universalattribute am übergebenen Element.
	 *
	 * @private
	 * @method
	 *
	 * @param {jQuery} element
	 *      jQuery-Element, dem die Attribute angehangen werden.
	 *
	 * @param {Object<string,*>} config Attribute.
	 * @param {string} [config.id]      <code>id</code>-Attribut.
	 * @param {string} [config.name]    <code>name</code>-Attribut.
	 * @param {string} [config.tooltip] <code>title</code>-Attribut.
	 * @param {string} [config.alt]     <code>alt</code>-Attribut.
	 *
	 * @return {jQuery} Parameter <code>element</code>.
	 */
	__setupCommonAttributes: function( element, config )
	{
		if( config.id )
		{
			element.attr('id', config.id );
		}
		if( config.name )
		{
			element.attr('name', config.name );
		}
		if( config.tooltip )
		{
			element.attr('title', config.tooltip );
		}
		if( config.alt )
		{
			element.attr('alt', config.alt );
		}
		return element;
	},

	/**
	 * Setzt die Konfiguration als Wert der Formular-Instanz, sofern das
	 * <code>name</code>-Attribut gesetzt ist.
	 *
	 * @private
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls.
	 *
	 * @param {string} config.name
	 *      Name/Schlüssel der Eigenschaft
	 *
	 * @param {gc.gis.util.GCForm}
	 *      Instanz des Formulars, wo die Eigenschaft hinterlegt werden soll.
	 */
	__storeInForm: function( config, form )
	{
		if( form && config && config.name )
		{
			form.set( config.name, config );
		}
	},

	/**
	 * Fügt dem Element des Controls ein <code>jQuery.on('change', ... )</code>
	 * hinzu.
	 *
	 * @private
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls.
	 *
	 * @param {function} fn
	 *      Funktion, die bei <code>jQuery.on('change', ... )</code> ausgeführt
	 *      wird.
	 * @param {Object}             fn.evt     jQuery-<code>change</code>-Event
	 * @param {gc.gis.util.GCForm} fn.form    Siehe Parameter <code>opt_form</code>
	 * @param {Object<string,*>}   fn.control Siehe Parameter <code>config</code>
	 *
	 * @param {gc.gis.util.GCForm} [opt_form]
	 *      Optionale Referenz auf das Formular, das als Parameter mitgeschickt
	 *      wird.
	 */
	__setupChangeEvent: function( control, fn, opt_form )
	{
		control._element.on('change', function(evt) {
			evt.preventDefault();
			try
			{
				fn.call( this, evt, opt_form, control );
			}
			catch( err )
			{
				gc.gis.iface.log.error( this, err );
			}
			jQuery( this ).blur();
		});
	},

	/**
	 * Fügt dem Element des Controls ein <code>jQuery.on('click', ... )</code>
	 * hinzu.
	 *
	 * @private
	 * @method
	 *
	 * @param {jQuery} element
	 *      Element, an das der <code>click</code>-Handler angedockt wird.
	 *
	 * @param {function} fn
	 *      Funktion, die bei <code>jQuery.on('click', ... )</code> ausgeführt
	 *      wird.
	 * @param {Object}             fn.evt     jQuery-<code>change</code>-Event
	 */
	__setupClickEvent: function( element, fn )
	{
		var self = this;

		element.on('click', function(evt) {
			evt.preventDefault();
			try
			{
				fn.call( this, evt );
				evt.preventDefault();
			}
			catch( err )
			{
				gc.gis.iface.log.error( self, err );
			}
			jQuery( this ).blur();
			return false;
		});
	},

	/**
	 * Erweitert das Control-Objekt um eine <code>getElement</code>-Funktion,
	 * initialisiert das Click-Event
	 * (siehe {@link gc.gis.util.GCFormRenderer.__setupClickEvent}) und führt
	 * die <code>initFunc</code> aus.
	 *
	 * @private
	 * @method
	 *
	 * @param {Object<string,*>} config
	 *      Konfiguration des Controls.
	 *
	 * @param {function} [config.click]
	 *      Ruft {@link gc.gis.util.GCFormRenderer.__setupClickEvent} auf, wenn
	 *      definiert.
	 *
	 * @param {function} [config.initFunc]
	 *      Funktion, die einmalig in
	 *      {@link gc.gis.util.GCFormRenderer.renderControl} aufgerufen wird.
	 *      Geeignet zum setzen von Listenern.
	 */
	__initControl: function( config )
	{
		try
		{
			config.getElement = function() {
				return this._element;
			};
			config.getWidget = function() {
				return this._widget;
			};
			if (typeof config.click === 'function')
			{
				this.__setupClickEvent( config._element, config.click )
			}
			if (typeof config.initFunc === 'function')
			{
				config.initFunc.call( this, config );
			}
		}
		catch (err)
		{
			gc.gis.iface.log.error(this, err);
		}
	}

};
